import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Role } from '@ready/shared';
import { useApi, type MyOrg } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/*
 * The organization the app shell's org section shows. An org page in the URL
 * wins (including a child org a parent admin is viewing); otherwise the last
 * one picked in the switcher (remembered in this browser), else the first of
 * the user's orgs. Remembering it is a convenience only: the server checks
 * access on every request.
 */

const KEY = 'ready.currentOrg';

const read = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};
const write = (id: string) => {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
};

export interface CurrentOrg {
  id: string;
  name: string;
  type: string;
  depth: number;
  parentId: string | null;
  logoUrl: string | null;
  /** The user's own role there; null when viewing as an admin of a parent org. */
  role: Role | null;
}

interface CurrentOrgValue {
  orgs: MyOrg[];
  loading: boolean;
  current: CurrentOrg | null;
  select: (id: string) => void;
}

const Ctx = createContext<CurrentOrgValue | null>(null);

export function CurrentOrgProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const myOrgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs, enabled: Boolean(user) });
  const orgs = useMemo(() => myOrgs.data?.orgs ?? [], [myOrgs.data]);
  const [picked, setPicked] = useState<string | null>(read);

  const routeOrgId = matchPath('/organizations/:orgId/*', pathname)?.params.orgId;
  const inRoute = routeOrgId && routeOrgId !== 'new' ? routeOrgId : null;

  // Visiting an org page makes it the current one.
  useEffect(() => {
    if (inRoute && inRoute !== picked) {
      setPicked(inRoute);
      write(inRoute);
    }
  }, [inRoute, picked]);

  const id =
    inRoute ?? (picked && orgs.some((o) => o.id === picked) ? picked : (orgs[0]?.id ?? null));
  const mine = orgs.find((o) => o.id === id);
  // A child org a parent admin is viewing isn't in their own list.
  const viewed = useQuery({
    queryKey: ['org', id],
    queryFn: () => api.org(id!),
    enabled: Boolean(id && !mine && myOrgs.isSuccess),
  });

  const current: CurrentOrg | null = mine
    ? { ...mine, parentId: mine.parentId ?? null, role: mine.role }
    : viewed.data
      ? {
          id: viewed.data.id,
          name: viewed.data.name,
          type: viewed.data.type,
          depth: viewed.data.depth,
          parentId: viewed.data.parentId ?? null,
          logoUrl: viewed.data.logoUrl ?? null,
          role: null,
        }
      : null;

  const select = useCallback((next: string) => {
    setPicked(next);
    write(next);
  }, []);

  return (
    <Ctx.Provider value={{ orgs, loading: myOrgs.isPending, current, select }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCurrentOrg() {
  const value = useContext(Ctx);
  if (!value) throw new Error('useCurrentOrg needs CurrentOrgProvider');
  return value;
}
